import { useEffect, useMemo, useRef } from 'react';
import { useLoaderData } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import { useActions } from '~/hooks/actions/use-actions';
import type { FormDataModel } from '../form-data-model.ts';
import {
  FormScreenStateStore,
  setFormScreenRuntime,
  type FormScreenRuntime,
} from '../form-screen-state.ts';
import { registerFormScreenActions } from '../form-screen-actions.ts';
import { DEFAULT_FORM_VALIDATION_MESSAGES } from '../form-validation-messages.ts';

type UseFormScreenRuntimeInput = {
  formDataModel?: FormDataModel | null;
  stateStore?: FormScreenStateStore;
};

/**
 * Activates form screen state, runtime registry, and action handlers.
 *
 * @param input - Optional form data model and shared state store.
 * @returns Active form screen state store.
 */
export function useFormScreenRuntime(
  input: UseFormScreenRuntimeInput = {}
): FormScreenStateStore {
  const { screen } = useLoaderData<typeof rootLoader>();
  const { registry } = useActions();
  const internalStore = useMemo(() => new FormScreenStateStore(), []);
  const stateStore = input.stateStore ?? internalStore;
  const formDataModelRef = useRef<FormDataModel | null>(
    input.formDataModel ?? null
  );

  formDataModelRef.current = input.formDataModel ?? null;

  useEffect(() => {
    const runtime: FormScreenRuntime = {
      screenId: screen.id,
      state: stateStore,
      get formDataModel() {
        return formDataModelRef.current;
      },
      get validationMessages() {
        return (
          formDataModelRef.current?.getValidationMessages() ??
          DEFAULT_FORM_VALIDATION_MESSAGES
        );
      },
    } as FormScreenRuntime;

    setFormScreenRuntime(runtime);

    const unregisterActions = registerFormScreenActions({
      register: (type, handler) => registry.register(type, handler),
    });

    const unsubscribeValidation = stateStore.subscribe(() => {
      const model = formDataModelRef.current;
      if (!model) return;
      model.requestComponentValidation(stateStore.getData());
    });

    return () => {
      unsubscribeValidation();
      unregisterActions();
      setFormScreenRuntime(null);
    };
  }, [registry, screen.id, stateStore]);

  return stateStore;
}
